import {
  actionTypes as asyncTypes,
  isAsyncOperation,
  isAsyncComplete,
  isAsyncFailure,
} from "async-ops";
import {
  AuditListItemResponse,
  AuditDetailsResponse,
  AuditKpiResponse,
  AuditUrgencyItemResponse,
  TypeaheadResponse,
  PagedResult,
  Facet,
} from "../types";
import {
  GET_INTERNAL_AUDIT_LIST,
  GET_AUDIT_DETAILS,
  GET_AUDIT_KPIS,
  GET_AUDIT_URGENCY,
  GET_AUDIT_TYPES,
  GET_AUDIT_AUDITORS,
  GET_AUDIT_PERIODS,
} from "../services/audit-queue-service";

// ─── State Shape ───────────────────────────────────────────────────────────────

export interface AuditQueueState {
  // List view
  list: {
    items: AuditListItemResponse[];
    totalCount: number;
    page: number;
    pageSize: number;
    hasNext: boolean;
    facets: Facet[];
    loading: boolean;
    error: string | null;
  };

  // Detail view
  detail: {
    item: AuditDetailsResponse | null;
    auditId: number | null;
    loading: boolean;
    error: string | null;
  };

  // KPIs
  kpis: {
    data: AuditKpiResponse | null;
    loading: boolean;
    error: string | null;
  };

  // Urgency list
  urgency: {
    items: AuditUrgencyItemResponse[];
    loading: boolean;
    error: string | null;
  };

  // Typeaheads
  typeaheads: {
    types: TypeaheadResponse[];
    auditors: TypeaheadResponse[];
    periods: TypeaheadResponse[];
    loading: boolean;
    error: string | null;
  };

  // UI state
  ui: {
    myAuditsOnly: boolean;
    sortBy?: string;
    sortDir?: "asc" | "desc";
    searchTerm: string;
    selectedAuditId: number | null;
    showDetailPanel: boolean;
  };
}

const initialState: AuditQueueState = {
  list: {
    items: [],
    totalCount: 0,
    page: 1,
    pageSize: 25,
    hasNext: false,
    facets: [],
    loading: false,
    error: null,
  },
  detail: {
    item: null,
    auditId: null,
    loading: false,
    error: null,
  },
  kpis: {
    data: null,
    loading: false,
    error: null,
  },
  urgency: {
    items: [],
    loading: false,
    error: null,
  },
  typeaheads: {
    types: [],
    auditors: [],
    periods: [],
    loading: false,
    error: null,
  },
  ui: {
    myAuditsOnly: false,
    searchTerm: "",
    selectedAuditId: null,
    showDetailPanel: false,
  },
};

// ─── Reducer ───────────────────────────────────────────────────────────────────

const listSlice = (
  state: AuditQueueState["list"],
  action: any,
  key: string
): AuditQueueState["list"] => {
  if (isAsyncOperation(key)(action)) {
    return { ...state, loading: true, error: null };
  }
  if (isAsyncComplete(key)(action)) {
    const response = action.response as PagedResult<AuditListItemResponse> | null;
    return {
      items: response?.items ?? [],
      totalCount: response?.totalCount ?? 0,
      page: response?.page ?? state.page,
      pageSize: response?.pageSize ?? state.pageSize,
      hasNext: response?.hasNext ?? false,
      // Only a call that opted into facets carries an authoritative block. A
      // rows-only call (paging, sorting) returns none, so the previous block is
      // kept rather than emptying the filter dropdowns.
      facets: response?.facetsRequested ? response.facets ?? [] : state.facets,
      loading: false,
      error: null,
    };
  }
  if (isAsyncFailure(key)(action)) {
    const errorMsg = action.error?.message || "Failed to load audits";
    console.error('[listSlice] FAILURE - Error:', errorMsg);
    return { ...state, loading: false, error: errorMsg };
  }
  return state;
};

const detailSlice = (
  state: AuditQueueState["detail"],
  action: any,
  key: string
): AuditQueueState["detail"] => {
  if (isAsyncOperation(key)(action)) {
    return { ...state, loading: true, error: null };
  }
  if (isAsyncComplete(key)(action)) {
    const response = action.response as AuditDetailsResponse | null;
    return {
      item: response ?? null,
      auditId: response?.auditId ?? null,
      loading: false,
      error: null,
    };
  }
  if (isAsyncFailure(key)(action)) {
    return { ...state, loading: false, error: "Failed to load audit details" };
  }
  return state;
};

const kpisSlice = (
  state: AuditQueueState["kpis"],
  action: any,
  key: string
): AuditQueueState["kpis"] => {
  if (isAsyncOperation(key)(action)) {
    return { ...state, loading: true, error: null };
  }
  if (isAsyncComplete(key)(action)) {
    const response = action.response as AuditKpiResponse | null;
    return { data: response ?? null, loading: false, error: null };
  }
  if (isAsyncFailure(key)(action)) {
    return { ...state, loading: false, error: "Failed to load KPIs" };
  }
  return state;
};

const urgencySlice = (
  state: AuditQueueState["urgency"],
  action: any,
  key: string
): AuditQueueState["urgency"] => {
  if (isAsyncOperation(key)(action)) {
    return { ...state, loading: true, error: null };
  }
  if (isAsyncComplete(key)(action)) {
    const response = action.response as AuditUrgencyItemResponse[];
    return { items: response ?? [], loading: false, error: null };
  }
  if (isAsyncFailure(key)(action)) {
    return { ...state, loading: false, error: "Failed to load urgency list" };
  }
  return state;
};

const typeaheadsSlice = (
  state: AuditQueueState["typeaheads"],
  action: any
): AuditQueueState["typeaheads"] => {
  if (isAsyncOperation(GET_AUDIT_TYPES)(action) || isAsyncOperation(GET_AUDIT_AUDITORS)(action) || isAsyncOperation(GET_AUDIT_PERIODS)(action)) {
    return { ...state, loading: true, error: null };
  }
  if (isAsyncComplete(GET_AUDIT_TYPES)(action)) {
    const response = action.response as TypeaheadResponse[];
    return { ...state, types: response ?? [], loading: false, error: null };
  }
  if (isAsyncComplete(GET_AUDIT_AUDITORS)(action)) {
    const response = action.response as TypeaheadResponse[];
    return { ...state, auditors: response ?? [], loading: false, error: null };
  }
  if (isAsyncComplete(GET_AUDIT_PERIODS)(action)) {
    const response = action.response as TypeaheadResponse[];
    return { ...state, periods: response ?? [], loading: false, error: null };
  }
  if (isAsyncFailure(GET_AUDIT_TYPES)(action) || isAsyncFailure(GET_AUDIT_AUDITORS)(action) || isAsyncFailure(GET_AUDIT_PERIODS)(action)) {
    return { ...state, loading: false, error: "Failed to load typeahead data" };
  }
  return state;
};

export const auditQueueReducer = (
  state = initialState,
  action: any
): AuditQueueState => {
  switch (action.type) {
    case asyncTypes.OPERATION:
    case asyncTypes.COMPLETE:
    case asyncTypes.FAILURE:
      return {
        ...state,
        list: listSlice(state.list, action, GET_INTERNAL_AUDIT_LIST),
        detail: detailSlice(state.detail, action, GET_AUDIT_DETAILS),
        kpis: kpisSlice(state.kpis, action, GET_AUDIT_KPIS),
        urgency: urgencySlice(state.urgency, action, GET_AUDIT_URGENCY),
        typeaheads: typeaheadsSlice(state.typeaheads, action),
      };

    case "AUDIT_QUEUE/SET_MY_AUDITS_ONLY":
      return {
        ...state,
        ui: { ...state.ui, myAuditsOnly: action.payload },
      };

    case "AUDIT_QUEUE/SET_SEARCH_TERM":
      return {
        ...state,
        ui: { ...state.ui, searchTerm: action.payload },
      };

    case "AUDIT_QUEUE/SET_SELECTED_AUDIT":
      return {
        ...state,
        ui: {
          ...state.ui,
          selectedAuditId: action.payload,
          showDetailPanel: action.payload !== null,
        },
      };

    case "AUDIT_QUEUE/CLOSE_DETAIL_PANEL":
      return {
        ...state,
        ui: { ...state.ui, selectedAuditId: null, showDetailPanel: false },
      };

    case "AUDIT_QUEUE/SET_SORT":
      return {
        ...state,
        ui: {
          ...state.ui,
          sortBy: action.payload.sortBy,
          sortDir: action.payload.sortDir,
        },
      };

    default:
      return state;
  }
};

// ─── Selectors ─────────────────────────────────────────────────────────────────

export const selectAuditList = (state: any): AuditQueueState["list"] => state.auditQueue?.list;
export const selectAuditDetail = (state: any): AuditQueueState["detail"] => state.auditQueue?.detail;
export const selectAuditKpis = (state: any): AuditQueueState["kpis"] => state.auditQueue?.kpis;
export const selectAuditUrgency = (state: any): AuditQueueState["urgency"] => state.auditQueue?.urgency;
export const selectAuditTypeaheads = (state: any): AuditQueueState["typeaheads"] =>
  state.auditQueue?.typeaheads;
export const selectAuditQueueUi = (state: any): AuditQueueState["ui"] => state.auditQueue?.ui;
